import { convertTimestamps } from "convert-firebase-timestamp";
import { v4 as uuidv4 } from 'uuid';
import { isValid } from 'date-fns';
import { AnyEntity, FbEntity, JsEntity, StringEntity } from "../_types/entity.type";
import { serverTimestamp, Timestamp } from "@angular/fire/firestore";

export function convertTimeStampsFb_Js(obj: FbEntity): JsEntity {
  return convertTimestamps(obj) as JsEntity;
}

export function convertTimeStampsJs_String(obj: AnyEntity | undefined) {
  if (!obj) return null;
  const newObj: { [key: string]: any } = {};
  for (let [key, value] of Object.entries(obj)) {
    if (value && isValid(value)) {
      newObj[key] = value.toString();
    } else {
      newObj[key] = value;
    }
  }
  return newObj as StringEntity;
}

export function convertTimeStampsString_Js(obj: StringEntity | undefined) {
  if (!obj) return null;
  const newObj: { [key: string]: any } = {};
  for (let [key, value] of Object.entries(obj)) {
    if (value && typeof value === 'string' && Date.parse(value) && isValid(Date.parse(value))) {
      newObj[key] = Date.parse(value);
    } else {
      newObj[key] = value;
    }
  }
  return newObj as JsEntity;
}

/**
 * Recursively convert nested objects, handling dates at any level
 */
function convertNestedObjectForFirestore(value: any): any {
  if (value === null || value === undefined) {
    return value;
  }

  // Check if it's a Firestore Timestamp-like object {seconds, nanoseconds}
  if (typeof value === 'object' && 'seconds' in value && 'nanoseconds' in value && Object.keys(value).length === 2) {
    return Timestamp.fromMillis(value.seconds * 1000 + value.nanoseconds / 1000000);
  }

  // Check if it's a Date object
  if (value instanceof Date && isValid(value)) {
    return Timestamp.fromMillis(value.getTime());
  }

  // Check if it's a valid date-like value (number timestamp)
  if (typeof value === 'number' && isValid(new Date(value))) {
    // Only convert if it looks like a millisecond timestamp (reasonable range)
    if (value > 946684800000 && value < 4102444800000) { // 2000-2100
      return Timestamp.fromMillis(value);
    }
  }

  // Recursively handle arrays
  if (Array.isArray(value)) {
    return value.map(item => convertNestedObjectForFirestore(item));
  }

  // Recursively handle plain objects
  if (typeof value === 'object' && value.constructor === Object) {
    const converted: { [key: string]: any } = {};
    for (const [k, v] of Object.entries(value)) {
      if (v === undefined) continue; // Skip undefined values
      converted[k] = convertNestedObjectForFirestore(v);
    }
    return converted;
  }

  return value;
}

export function convertTimeStampsJs_Fb(obj: Partial<JsEntity> | undefined): Partial<FbEntity> | null {
  if (!obj) return null;
  const newObj: { [key: string]: any } = {};
  for (let [key, value] of Object.entries(obj)) {
    // Skip undefined values - Firestore doesn't accept undefined
    if (value === undefined) {
      continue;
    }
    if (key.endsWith('At') && value && isValid(value)) {
      // Top-level date fields
      newObj[key] = Timestamp.fromMillis((value as Date).getTime ? (value as Date).getTime() : value as number);
    } else if (typeof value === 'object' && value !== null) {
      // Recursively convert nested objects (like publishedSnapshot)
      newObj[key] = convertNestedObjectForFirestore(value);
    } else {
      newObj[key] = value;
    }
  }
  newObj['cloudUpdatedAt'] = serverTimestamp();
  return newObj as FbEntity;
}

export function getRandomInt(min: number, max: number) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min) + min); // The maximum is exclusive and the minimum is inclusive
}

export function getRandomBoolean() {
  return Math.random() < 0.5;
}

export function getRandomBooleanArray(num: number) {
  return [...Array(num).keys()].map(i => getRandomBoolean());
}

export function getNewId() {
  return uuidv4();
}

import { isRtlLanguage, getLanguageName } from '../_datas/languages.data';

const CJK_CODES = new Set(['ja', 'ko', 'zh']);

/** Shared comparator: English first, then Latin alphabetical by name, then CJK, then RTL */
function languageComparator(a: string, b: string): number {
    if (a === 'en') return -1;
    if (b === 'en') return 1;

    const aRtl = isRtlLanguage(a);
    const bRtl = isRtlLanguage(b);
    if (aRtl !== bRtl) return aRtl ? 1 : -1;

    const aCjk = CJK_CODES.has(a);
    const bCjk = CJK_CODES.has(b);
    if (aCjk !== bCjk) return aCjk ? 1 : -1;

    // Sort by English name within groups (not by code)
    const aName = getLanguageName(a);
    const bName = getLanguageName(b);
    return aName.localeCompare(bName);
}

/**
 * Sort language codes: English first, Latin by name, CJK, then RTL
 * @param langCodes Array of language codes (e.g., ['de', 'en', 'fr'])
 * @returns Sorted array
 */
export function sortLanguageCodes(langCodes: string[]): string[] {
  return [...langCodes].sort(languageComparator);
}

/**
 * Sort language objects: English first, Latin by name, CJK, then RTL
 * @param languages Array of objects with languageCode property
 * @returns Sorted array
 */
export function sortLanguages<T extends { languageCode: string }>(languages: T[]): T[] {
  return [...languages].sort((a, b) => languageComparator(a.languageCode, b.languageCode));
}